import { useCallback } from 'react';
import {
  buildGtmEcommercePayload,
  GtmEcommerceParams,
  GtmEcommerceEventName,
  GtmPurchaseBehavior,
  GtmStatus,
  GtmPaymentType,
  GTM_PURCHASE_BEHAVIOR,
  GTM_STATUS,
} from '@/utils/analytics/gtm-ecommerce';
import { addToDataLayer } from '@/utils/analytics/gtm';
import { logger } from '@/utils';
import { EPlanIntervalUnit, IPlan } from '@/models/checkout.model';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';

export interface GtmEcommercePayload {
  readonly plan: IPlan;
  readonly paymentType?: GtmPaymentType;
}

interface UseGtmEcommerceResult {
  readonly fireGtmEcommerceEvent: (
    eventName: GtmEcommerceEventName,
    payload: GtmEcommercePayload
  ) => void;
}

/**
 * Hook for handling GTM ecommerce events.
 * Builds params with user context and sends to dataLayer.
 * Used internally by useAnalytics - don't call directly from components.
 */
export const useGtmEcommerce = (): UseGtmEcommerceResult => {
  const user = useSelector((state: RootState) => state.user);

  const getPurchaseBehavior = (plan: IPlan): GtmPurchaseBehavior => {
    return plan.trial_period_in_days > 0
      ? GTM_PURCHASE_BEHAVIOR.POST_TRIAL_IMMEDIATE
      : GTM_PURCHASE_BEHAVIOR.NON_TRIAL;
  };

  const getStatus = (plan: IPlan, hasCoupon: boolean): GtmStatus => {
    if (plan.interval_unit === EPlanIntervalUnit.LIFETIME) {
      return GTM_STATUS.LIFETIME_PREMIUM;
    }
    if (hasCoupon) {
      return GTM_STATUS.ACTIVE_COUPON;
    }
    return GTM_STATUS.ACTIVE;
  };

  const getDiscountAmount = (plan: IPlan): number | undefined => {
    if (
      !plan.price_before_discount ||
      plan.price_before_discount <= plan.price
    ) {
      return undefined;
    }
    return plan.price_before_discount - plan.price;
  };

  const buildGtmEcommerceParams = useCallback(
    (plan: IPlan, paymentType?: GtmPaymentType): GtmEcommerceParams => {
      const discountAmount = getDiscountAmount(plan);
      const couponCode = undefined; // Only exists on the old site
      const hasCoupon = !!discountAmount;

      return {
        plan,
        userId: user?.uid ?? '',
        status: getStatus(plan, hasCoupon),
        purchaseBehavior: getPurchaseBehavior(plan),
        paymentType,
        email: user?.email ?? undefined,
        phoneNumber: user?.phoneNumber ?? undefined,
        firstName: user?.displayName?.split(' ')[0] ?? undefined,
        lastName: user?.displayName?.split(' ').slice(1).join(' ') ?? undefined,
        couponCode,
        discountAmount,
      };
    },
    [user]
  );

  const fireGtmEcommerceEvent = useCallback(
    (eventName: GtmEcommerceEventName, payload: GtmEcommercePayload): void => {
      const { plan, paymentType } = payload;
      if (!plan) return;

      try {
        const params = buildGtmEcommerceParams(plan, paymentType);
        const gtmPayload = buildGtmEcommercePayload(eventName, params);
        addToDataLayer(gtmPayload);
      } catch (error) {
        logger.error(`GTM ${eventName} event failed`, error);
      }
    },
    [buildGtmEcommerceParams]
  );

  return {
    fireGtmEcommerceEvent,
  };
};
